import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../Component/Layout';
import axios from 'axios';
import Select from 'react-select';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CharacteristicsModal from "../../Component/CharacteristicsModal ";

import { PURCHASE_API, MASTER_API } from '../../apiConfig';

function PurchaseInfo() {
    const [masterData, setMasterData] = useState({
        groups: [], types: [], brands: [], categories: [], divisions: [], productCodes: [], vendors: []
    });
    const [filters, setFilters] = useState({
        code: null,
        description: null,
        productType: null,
        category: null,
        subCategory: null,
        vendorCode: null,
        vendor: null
    });

    // ── Cable attribute filters (mirrors Purchase Requisition's 2nd filter
    // row: Construction, Code, Cable Type, Category, Cores, Color, Cross
    // Section, Std. Ref, Voltage) — same shared product master, matched via
    // productCode against tableData rows. ──
    const [selectedConstruction, setSelectedConstruction] = useState(null);
    const [selectedCode, setSelectedCode] = useState(null);
    const [selectedCableType, setSelectedCableType] = useState(null);
    const [selectedCableCategory, setSelectedCableCategory] = useState(null);
    const [selectedCores, setSelectedCores] = useState(null);
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedCrossSection, setSelectedCrossSection] = useState(null);
    const [selectedStdRef, setSelectedStdRef] = useState(null);
    const [selectedVoltage, setSelectedVoltage] = useState(null);
    const [cableAttrProducts, setCableAttrProducts] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [assignmentTab, setAssignmentTab] = useState("assigned");
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 10;

    // --- Main Table Class Modal State ---
    const [showClassModal, setShowClassModal] = useState(false);
    const [classCharValues, setClassCharValues] = useState([]);
    const [classModalContext, setClassModalContext] = useState({ productCode: null, pirId: null, vendorName: null });

    // --- Bulk Assign State ---
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignRows, setAssignRows] = useState([]);

    // --- Single Row Manage Partners Modal ---
    const [showManageModal, setShowManageModal] = useState(false);
    const [activeProduct, setActiveProduct] = useState(null);
    const [singleAssignRows, setSingleAssignRows] = useState([]);
    const [isSingleEditMode, setIsSingleEditMode] = useState(false);

    // --- Characteristics Modal State (for Manage/Bulk modals) ---
    const [charModalVisible, setCharModalVisible] = useState(false);
    const [selectedChars, setSelectedChars] = useState([]);
    const [charModalContext, setCharModalContext] = useState({ type: null, id: null, pirNumber: null });
    const [isBulkEditMode, setIsBulkEditMode] = useState(false);

    // --- Excel Upload / Draft State ---
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showDraftModal, setShowDraftModal] = useState(false);
    const [draftsList, setDraftsList] = useState([]);
    const [conflictProductCodes, setConflictProductCodes] = useState([]);
    const [publishingProductCode, setPublishingProductCode] = useState(null);
    const [isPublishingAll, setIsPublishingAll] = useState(false);
    const [showDraftReplaceConfirm, setShowDraftReplaceConfirm] = useState(false);
    const [showDeleteRecordConfirm, setShowDeleteRecordConfirm] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null); // { pirNumber, productCode }
    const [draftReplaceTarget, setDraftReplaceTarget] = useState(null);
    const [showBulkReplaceConfirm, setShowBulkReplaceConfirm] = useState(false);
    const [showDraftCharModal, setShowDraftCharModal] = useState(false);
    const [draftCharValues, setDraftCharValues] = useState([]);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const [typ, cat, codes, tData, ven] = await Promise.all([

                    axios.get(`${PURCHASE_API}/pi/info_types`),

                    axios.get(`${PURCHASE_API}/pi/info_category`),
                    axios.get(`${PURCHASE_API}/pi/info_product_codes`),
                    axios.get(`${PURCHASE_API}/pi/info_table_data`),
                    axios.get(`${PURCHASE_API}/pi/vendors`)
                ]);
                const asArr = (d) => (Array.isArray(d) ? d : []);
                setMasterData({
                    types: asArr(typ.data), categories: asArr(cat.data),
                    productCodes: asArr(codes.data), vendors: asArr(ven.data)
                });
                setTableData(Array.isArray(tData.data) ? tData.data : []);
            } catch (err) {
                console.error("Failed to load filter data", err);
            }
        };
        fetchAll();

        // ── Shared product master list (Construction..Voltage) — same
        // endpoint the Purchase Requisition / Stock Report / BOM filters
        // use, so the cable-attribute filter options below are computed
        // from it client-side instead of needing separate endpoints. ──
        axios.get(`${MASTER_API}/bom/products`)
            .then(res => setCableAttrProducts(res.data.data || []))
            .catch(err => console.error("Failed loading products for cable attribute filters:", err.response?.data || err.message));
    }, []);

    // ─── ADD THIS NEW EFFECT ───
    useEffect(() => {
        loadPurchaseDrafts();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [filters, selectedConstruction, selectedCode, selectedCableType, selectedCableCategory,
        selectedCores, selectedColor, selectedCrossSection, selectedStdRef, selectedVoltage]);
    useEffect(() => {
        setSelectedRows([]);
    }, [filters, selectedConstruction, selectedCode, selectedCableType, selectedCableCategory,
        selectedCores, selectedColor, selectedCrossSection, selectedStdRef, selectedVoltage]);

    useEffect(() => {
        setCurrentPage(1);
    }, [assignmentTab]);

    // Cable attribute lookup — keyed by productCode so each PurchaseInfo row
    // (assetdetails/productdetails combined) can be matched against the
    // shared product master's Construction..Voltage columns.
    const cableAttrMap = useMemo(() => {
        const map = new Map();
        cableAttrProducts.forEach(p => {
            if (p.productCode) map.set(String(p.productCode).trim(), p);
        });
        return map;
    }, [cableAttrProducts]);

    const getCableAttrOptions = React.useCallback((key) => {
        const uniqueVals = Array.from(
            new Set(
                cableAttrProducts
                    .map(p => p[key])
                    .filter(v => v !== undefined && v !== null && v !== "")
                    .map(v => String(v).trim())
                    .filter(v => v !== "")
            )
        );
        return uniqueVals
            .map(v => ({ value: v, label: v }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [cableAttrProducts]);

    const constructionOptions = useMemo(() => getCableAttrOptions("construction"), [getCableAttrOptions]);
    const codeOptions = useMemo(() => getCableAttrOptions("code"), [getCableAttrOptions]);
    const cableTypeOptions = useMemo(() => getCableAttrOptions("cableType"), [getCableAttrOptions]);
    const cableCategoryOptions = useMemo(() => getCableAttrOptions("category"), [getCableAttrOptions]);
    const coresOptions = useMemo(() => getCableAttrOptions("noOfCores"), [getCableAttrOptions]);
    const colorOptions = useMemo(() => getCableAttrOptions("color"), [getCableAttrOptions]);
    const crossSectionOptions = useMemo(() => getCableAttrOptions("nominalCrossSection"), [getCableAttrOptions]);
    const stdRefOptions = useMemo(() => getCableAttrOptions("referenceOfStandard"), [getCableAttrOptions]);
    const voltageOptions = useMemo(() => getCableAttrOptions("voltage"), [getCableAttrOptions]);

    const filteredProducts = useMemo(() => {
        const seen = new Set();
        return tableData.filter(product => {
            // NEW — tab filter first
            const isAssigned = !!product.pir_number;
            if (assignmentTab === "assigned" && !isAssigned) return false;
            if (assignmentTab === "unassigned" && isAssigned) return false;

            if (filters.code && product.productCode !== filters.code.value) return false;
            if (filters.description && product.productDescription !== filters.description.value) return false;
            if (filters.productType && product.productType !== filters.productType.value) return false;
            if (filters.category && product.productCategory !== filters.category.value) return false;
            if (filters.subCategory && product.subCategory !== filters.subCategory.value) return false;
            if (filters.vendorCode && product.vendorCode !== filters.vendorCode.value)
                return false;

            if (filters.vendor && product.vendor !== filters.vendor.value)
                return false;

            // ── Cable attribute filters — matched via the shared product
            // master, joined on productCode ──
            if (selectedConstruction || selectedCode || selectedCableType || selectedCableCategory ||
                selectedCores || selectedColor || selectedCrossSection || selectedStdRef || selectedVoltage) {
                const attrs = cableAttrMap.get(String(product.productCode).trim());
                if (!attrs) return false;
                if (selectedConstruction && attrs.construction !== selectedConstruction.value) return false;
                if (selectedCode && attrs.code !== selectedCode.value) return false;
                if (selectedCableType && attrs.cableType !== selectedCableType.value) return false;
                if (selectedCableCategory && attrs.category !== selectedCableCategory.value) return false;
                if (selectedCores && String(attrs.noOfCores) !== String(selectedCores.value)) return false;
                if (selectedColor && attrs.color !== selectedColor.value) return false;
                if (selectedCrossSection && String(attrs.nominalCrossSection) !== String(selectedCrossSection.value)) return false;
                if (selectedStdRef && attrs.referenceOfStandard !== selectedStdRef.value) return false;
                if (selectedVoltage && attrs.voltage !== selectedVoltage.value) return false;
            }

            // Deduplicate by productCode + vendor combination
            const key = `${product.productCode}_${product.vendor || ''}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [tableData, filters, assignmentTab, cableAttrMap,
        selectedConstruction, selectedCode, selectedCableType, selectedCableCategory,
        selectedCores, selectedColor, selectedCrossSection, selectedStdRef, selectedVoltage]);

    // Sub Category options are interconnected with Category:
    // if a Category is selected, only Sub Categories belonging to that Category are shown.
    const subCategoryOptions = useMemo(() => {
        const relevant = filters.category
            ? tableData.filter(p => p.productCategory === filters.category.value)
            : tableData;
        return [...new Map(
            relevant
                .filter(p => p.subCategory)
                .map(p => [p.subCategory, { value: p.subCategory, label: p.subCategory }])
        ).values()];
    }, [tableData, filters.category]);

    // FIX: never return an object as a label (React error #31) and skip
    // incomplete rows (e.g. supplier with NULL code/name in the AWS DB).
    const getOptions = (list) => {
        if (!Array.isArray(list)) return [];
        return list
            .map(i => {
                if (i === null || i === undefined) return null;
                if (typeof i !== 'object') return { value: i, label: String(i) };
                const value = i.code ?? i.name ?? i.partner_code;
                const label = i.name ?? i.code ?? i.vendor_name;
                if (value == null || value === '' || label == null || label === '') return null;
                return { value, label: String(label) };
            })
            .filter(Boolean);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedRows(filteredProducts.map(p => p.productCode));
        else setSelectedRows([]);
    };

    const handleSelectRow = (code) => {
        setSelectedRows(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
    };

    // ─── Fetch characteristics for a class ───
    const fetchClassCharacteristics = async (classCode) => {
        if (!classCode) return [];
        try {
            const res = await axios.get(
                `${PURCHASE_API}/pi/class_characteristics/${encodeURIComponent(classCode)}`
            );
            return res.data.map(row => ({
                characteristic: row.characteristic_name || row.characteristic || row.name || '',
                value: ''
            }));
        } catch (e) {
            console.error("Failed to fetch class characteristics", e);
            return [];
        }
    };

    // ============================================================================
    // MAIN TABLE: Class Characteristics Modal (opens from main table ▶ button)
    // ============================================================================
    const openClassCharacteristics = async (product) => {
        if (!product.productClass) {
            return toast.warning("No class assigned to this product.");
        }

        try {
            const pirRes = await axios.post(
                `${PURCHASE_API}/pi/get_assigned_vendors`,
                {
                    productCodes: [product.productCode]
                }
            );

            const pirRecords = pirRes.data;

            // Use clicked row PIR instead of default PIR
            const selectedPir =
                pirRecords.find(
                    r => r.pir_number === product.pir_number
                ) || pirRecords[0];

            const pirId = selectedPir?.pir_number || null;
            const vendorName = selectedPir?.vendorName || null;

            setClassModalContext({
                productCode: product.productCode,
                pirId,
                vendorName
            });

            const savedValues = {};

            if (selectedPir?.characteristics) {
                let chars = selectedPir.characteristics;

                if (typeof chars === "string") {
                    try {
                        chars = JSON.parse(chars);
                    } catch (e) {
                        chars = [];
                    }
                }

                if (Array.isArray(chars)) {
                    chars.forEach(c => {
                        const name = c.characteristic || c.name || c;
                        if (name) {
                            savedValues[
                                String(name).toLowerCase()
                            ] = c.value || "";
                        }
                    });
                }
            }

            const freshChars = await fetchClassCharacteristics(
                product.productClass
            );

            const merged = freshChars.map(item => ({
                characteristic: item.characteristic,
                value: savedValues[String(item.characteristic).toLowerCase()] || ""
            }));

            setClassCharValues(merged);
            setShowClassModal(true);
        } catch (e) {
            toast.error("Failed to load characteristics.");
        }
    };

    const handleClassCharChange = (idx, val) => {
        const updated = [...classCharValues];
        updated[idx].value = val;
        setClassCharValues(updated);
    };

    const handleSaveClassCharacteristics = async () => {
        const { productCode, pirId, vendorName } = classModalContext;
        if (!pirId) {
            toast.warning("No PIR found for this product. Please assign a vendor first.");
            setShowClassModal(false);
            setClassCharValues([]);
            setClassModalContext({ productCode: null, pirId: null, vendorName: null });
            return;
        }
        try {
            await axios.post(`${PURCHASE_API}/pi/save_class_characteristics`, {
                pirId, vendorName, productCode, characteristics: classCharValues
            });
            toast.success("Characteristics saved successfully!");
        } catch (e) {
            console.error(e);
            toast.error("Failed to save characteristics.");
        }
        setShowClassModal(false);
        setClassCharValues([]);
        setClassModalContext({ productCode: null, pirId: null, vendorName: null });
    };

    const handleCloseClassModal = () => {
        setShowClassModal(false);
        setClassCharValues([]);
        setClassModalContext({ productCode: null, pirId: null, vendorName: null });
    };

    // ============================================================================
    // Characteristics Modal for Manage Partners / Bulk Assign modals
    // ============================================================================
    const handleOpenCharModal = async (type, rowId, rowData) => {
        setCharModalContext({ type, id: rowId, pirNumber: rowData.pirNumber || null });

        let existingChars = rowData.characteristics || [];
        if (typeof existingChars === 'string') {
            try { existingChars = JSON.parse(existingChars); } catch (e) { existingChars = []; }
        }

        const existingValuesMap = {};
        if (Array.isArray(existingChars)) {
            existingChars.forEach(c => {
                const name = c.characteristic || c.name || c;
                if (name) existingValuesMap[String(name).toLowerCase()] = c.value || '';
            });
        }

        if (rowData.productClass) {
            const freshChars = await fetchClassCharacteristics(rowData.productClass);
            const merged = freshChars.map(c => ({
                characteristic: c.characteristic,
                value: existingValuesMap[String(c.characteristic).toLowerCase()] || ""
            }));
            setSelectedChars(merged);
        } else {
            setSelectedChars(Array.isArray(existingChars) && existingChars.length > 0 ? existingChars : []);
        }

        setCharModalVisible(true);
    };

    const handleCharChange = (idx, val) => {
        const updated = [...selectedChars];
        updated[idx].value = val;
        setSelectedChars(updated);
    };

    // Save chars back to row state (called by modal's Save button)
    const handleSaveCharModal = () => {
        const { type, id } = charModalContext;
        if (type === 'bulk') {
            setAssignRows(prev => prev.map(r => r.id === id ? { ...r, characteristics: selectedChars } : r));
        } else if (type === 'single') {
            setSingleAssignRows(prev => prev.map(r => r.id === id ? { ...r, characteristics: selectedChars } : r));
        }
        setCharModalVisible(false);
    };

    // Close without saving (called by modal's Close button)
    const handleCloseCharModal = () => {
        const { type, id } = charModalContext;
        if (type === 'bulk') {
            setAssignRows(prev => prev.map(r => r.id === id ? { ...r, characteristics: selectedChars } : r));
        } else if (type === 'single') {
            setSingleAssignRows(prev => prev.map(r => r.id === id ? { ...r, characteristics: selectedChars } : r));
        }
        setCharModalVisible(false);
    };
    // ============================================================================
    // 1. BULK ASSIGN LOGIC
    // ============================================================================
    const handleOpenBulkAssign = async () => {
        if (selectedRows.length === 0) return toast.warning("Please select at least one product first.");

        try {
            const response = await axios.post(`${PURCHASE_API}/pi/get_assigned_vendors`, {
                productCodes: selectedRows
            });
            const existingAssignments = response.data;

            // Check if ANY selected product already has assignments
            const hasExisting = existingAssignments.length > 0;

            const initialAssignData = selectedRows.flatMap(code => {
                const product = tableData.find(p => p.productCode === code);
                const existing = existingAssignments.filter(r => r.productCode === code);

                if (existing.length > 0) {
                    return existing.map(record => {
                        let parsedChars = record.characteristics;
                        if (typeof parsedChars === 'string') {
                            try { parsedChars = JSON.parse(parsedChars); } catch (e) { parsedChars = []; }
                        }
                        return {
                            id: Math.random().toString(36).substr(2, 9),
                            productCode: record.productCode,
                            productDescription: product.productDescription,
                            baseUnit: product.baseUnit,
                            uom: record.uom,
                            productClass: record.productClass,
                            characteristics: Array.isArray(parsedChars) ? parsedChars : [],
                            baseUnit: record.baseUnit || product.baseUnit,
                            productCost: record.productCost || 0,
                            vendor: { value: record.vendorCode, label: record.vendorName },
                            isDefault: record.isDefault === 1 || record.isDefault === true,
                            pirNumber: record.pir_number || null
                        };
                    });
                } else {
                    return [{
                        id: Math.random().toString(36).substr(2, 9),
                        productCode: product.productCode,
                        productDescription: product.productDescription,
                        uom: product.uom,
                        productClass: product.productClass,
                        characteristics: [],
                        productCost: product.movingAveragePrice || 0,
                        vendor: null,
                        isDefault: true,
                        pirNumber: null
                    }];
                }
            });

            setAssignRows(initialAssignData);
            setIsBulkEditMode(!hasExisting); // ← view mode if existing, edit mode if all new
            setShowAssignModal(true);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load existing assignments.");
        }
    };

    const handleAddBulkRow = (productCode) => {
        const product = tableData.find(p => p.productCode === productCode);
        setAssignRows(prev => [...prev, {
            id: Math.random().toString(36).substr(2, 9),
            productCode: product.productCode,
            productDescription: product.productDescription,
            uom: product.uom,
            productClass: product.productClass,
            characteristics: [],
            baseUnit: product.baseUnit,
            productCost: product.movingAveragePrice || 0,
            vendor: null,
            isDefault: false
        }]);
    };

    const handleSaveBulkAssignments = async () => {
        const isValid = assignRows.every(row => row.vendor !== null);
        if (!isValid && assignRows.length > 0) return toast.error("Please select a supplier for all rows.");


        const uniqueProductCodes = [...new Set(assignRows.map(r => r.productCode))];
        for (let code of uniqueProductCodes) {
            const rowsForProduct = assignRows.filter(r => r.productCode === code);
            const hasDefault = rowsForProduct.some(r => r.isDefault === true);
            if (!hasDefault && rowsForProduct.length > 0) {
                return toast.error(`Please select a Default Supplier for product: ${code}`);
            }
        }

        try {
            const response = await axios.post(`${PURCHASE_API}/pi/save_purchase_info`, {
                assignments: assignRows,
                selectedProductCodes: selectedRows
            });
            const pirNumbers = response.data.pirNumbers || [];
            toast.success(pirNumbers.length === 1 ? `Saved! PIR Number: ${pirNumbers[0]}` : `Saved! PIR Numbers: ${pirNumbers.join(', ')}`);
            setShowAssignModal(false);
            setAssignRows([]);
            setSelectedRows([]);
            const tData = await axios.get(`${PURCHASE_API}/pi/info_table_data`);
            setTableData(Array.isArray(tData.data) ? tData.data : []);
        } catch (error) {
            console.error(error);
            toast.error("Failed to update assignments.");
        }
    };

    // ============================================================================
    // 2. SINGLE ROW MANAGE LOGIC
    // ============================================================================
    const handleManageSingleProduct = async (product) => {
        setActiveProduct(product);
        try {
            const response = await axios.post(`${PURCHASE_API}/pi/get_assigned_vendors`, {
                productCodes: [product.productCode]
            });
            const existingAssignments = response.data;

            let initialRows = [];
            if (existingAssignments.length > 0) {
                initialRows = existingAssignments.map(record => {
                    let parsedChars = record.characteristics;
                    if (typeof parsedChars === 'string') {
                        try { parsedChars = JSON.parse(parsedChars); } catch (e) { parsedChars = []; }
                    }
                    return {
                        id: Math.random().toString(36).substr(2, 9),
                        pirNumber: record.pir_number || null,
                        productCode: record.productCode,
                        moq: product.moq || 0,
                        uom: record.uom,
                        productClass: record.productClass,
                        characteristics: Array.isArray(parsedChars) ? parsedChars : [],
                        productCost: record.productCost,
                        vendor: { value: record.vendorCode, label: record.vendorName },
                        isDefault: record.isDefault === 1 || record.isDefault === true
                    };
                });
            } else {
                initialRows.push({
                    id: Math.random().toString(36).substr(2, 9),
                    productCode: product.productCode,
                    moq: product.moq || 0,
                    uom: product.uom,
                    productClass: product.productClass,
                    characteristics: [],
                    baseUnit: product.baseUnit,
                    productCost: product.movingAveragePrice || 0,
                    vendor: null,
                    isDefault: true
                });
            }

            setSingleAssignRows(initialRows);
            // If existing assignments → view mode (Edit button shown)
            // If no assignments → edit mode (Save button shown directly)
            setIsSingleEditMode(existingAssignments.length === 0);
            setShowManageModal(true);
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch assigned vendors.");
        }
    };

    const handleAddSingleRow = () => {
        setSingleAssignRows(prev => [...prev, {
            id: Math.random().toString(36).substr(2, 9),
            productCode: activeProduct.productCode,
            uom: activeProduct.uom,
            productClass: activeProduct.productClass,
            characteristics: [],
            baseUnit: activeProduct.baseUnit,
            productCost: activeProduct.productCost || 0,
            vendor: null,
            isDefault: false
        }]);
    };

    const handleSaveSingleManage = async () => {
        const isValid = singleAssignRows.every(row => row.vendor !== null);
        if (!isValid && singleAssignRows.length > 0) return toast.error("Please select a supplier for all rows.");

        const hasDefault = singleAssignRows.some(r => r.isDefault === true);
        if (!hasDefault && singleAssignRows.length > 0) return toast.error("Please select a Default Supplier.");
        try {
            const response = await axios.post(`${PURCHASE_API}/pi/save_purchase_info`, {
                assignments: singleAssignRows,
                selectedProductCodes: [activeProduct.productCode]
            });
            const pirNumbers = response.data.pirNumbers || [];

            toast.success(pirNumbers.length === 1 ? `Saved! PIR Number: ${pirNumbers[0]}` : `Saved! PIR Numbers: ${pirNumbers.join(', ')}`);
            setShowManageModal(false);
            setIsSingleEditMode(false);
            setSingleAssignRows([]);
            const tData = await axios.get(`${PURCHASE_API}/pi/info_table_data`);
            setTableData(Array.isArray(tData.data) ? tData.data : []);
        } catch (error) {
            toast.error("Failed to update assignments.");
        }
    };

    const handleAssignRowChange = (id, field, value, isSingleModal = false) => {
        const setter = isSingleModal ? setSingleAssignRows : setAssignRows;
        setter(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row));
    };

    const handleSetDefault = (id, productCode, isSingleModal = false) => {
        const setter = isSingleModal ? setSingleAssignRows : setAssignRows;
        setter(prev => prev.map(row =>
            row.productCode === productCode ? { ...row, isDefault: row.id === id } : row
        ));
    };

    const handleRemoveAssignRow = (id, isSingleModal = false) => {
        if (isSingleModal) setSingleAssignRows(prev => prev.filter(row => row.id !== id));
        else setAssignRows(prev => prev.filter(row => row.id !== id));
    };

    // ─── ADD ALL OF THIS ───
    const loadPurchaseDrafts = async () => {
        try {
            const res = await axios.get(`${PURCHASE_API}/pi/get-drafts`);
            if (res.data.success) {
                setDraftsList(res.data.drafts || []);
                setConflictProductCodes(res.data.conflictProductCodes || []);
            }
        } catch (err) {
            console.error("Failed to load purchase drafts", err);
        }
    };

    const handlePurchaseExcelUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const uploadData = new FormData();
        uploadData.append("file", file);

        try {
            const res = await axios.post(`${PURCHASE_API}/pi/parse-excel`, uploadData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            toast.success(res.data.message);
            await loadPurchaseDrafts();
            setShowUploadModal(false);
            setShowDraftModal(true);
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to upload Excel file");
        } finally {
            e.target.value = null;
        }
    };

    const handlePublishPurchaseDraft = async (productCode, replaceExisting = false) => {
        setPublishingProductCode(productCode);
        try {
            const res = await axios.post(`${PURCHASE_API}/pi/publish-draft`, { productCode, replaceExisting });
            if (res.data.success) {
                toast.success(res.data.message);
                setConflictProductCodes(prev => prev.filter(c => c !== productCode));
                await loadPurchaseDrafts();
                const tData = await axios.get(`${PURCHASE_API}/pi/info_table_data`);
                setTableData(Array.isArray(tData.data) ? tData.data : []);
            }
        } catch (err) {
            toast.error("Failed to publish draft");
        } finally {
            setPublishingProductCode(null);
        }
    };

    const onClickPublishPurchaseDraft = (productCode) => {
        if (conflictProductCodes.includes(productCode)) {
            setDraftReplaceTarget(productCode);
            setShowDraftReplaceConfirm(true);
        } else {
            handlePublishPurchaseDraft(productCode, false);
        }
    };

    const handleDeletePurchaseDraft = async (productCode) => {
        try {
            await axios.delete(`${PURCHASE_API}/pi/drafts/product/${encodeURIComponent(productCode)}`);
            toast.success("Draft deleted");
            setDraftsList(prev => prev.filter(d => d.productCode !== productCode));
            setConflictProductCodes(prev => prev.filter(c => c !== productCode));
        } catch (err) {
            toast.error("Failed to delete draft");
        }
    };

    const publishAllPurchaseDraftsApi = async (replaceExisting) => {
        setIsPublishingAll(true);
        try {
            const res = await axios.post(`${PURCHASE_API}/pi/publish-all-drafts`, { replaceExisting });
            if (res.data.success) {
                toast.success("Drafts published");
                await loadPurchaseDrafts();
                const tData = await axios.get(`${PURCHASE_API}/pi/info_table_data`);
                setTableData(Array.isArray(tData.data) ? tData.data : []);
                setShowDraftModal(false);
            }
        } catch (err) {
            toast.error("Failed to publish drafts");
        } finally {
            setIsPublishingAll(false);
        }
    };

    const handlePublishAllPurchaseDrafts = () => {
        if (draftsList.length === 0) return;
        const hasConflicts = draftsList.some(d => conflictProductCodes.includes(d.productCode));
        if (hasConflicts) {
            setShowBulkReplaceConfirm(true);
            return;
        }
        publishAllPurchaseDraftsApi(false);
    };

    const handleDeletePurchaseDraftRow = async (id) => {
        try {
            await axios.delete(`${PURCHASE_API}/pi/drafts/${id}`);
            toast.success("Supplier row deleted");
            setDraftsList(prev =>
                prev
                    .map(g => ({ ...g, rows: g.rows.filter(r => r.id !== id) }))
                    .filter(g => g.rows.length > 0)
            );
        } catch (err) {
            toast.error("Failed to delete supplier row");
        }
    };

    const handleDeleteRecord = (pirNumber, productCode) => {
        setRecordToDelete({ pirNumber, productCode });
        setShowDeleteRecordConfirm(true);
    };

    const confirmDeleteRecord = async () => {
        const target = recordToDelete;
        setShowDeleteRecordConfirm(false);
        if (!target?.pirNumber) return;

        try {
            await axios.delete(`${PURCHASE_API}/pi/records/${encodeURIComponent(target.pirNumber)}`);
            toast.success("Purchase record deleted");
            const tData = await axios.get(`${PURCHASE_API}/pi/info_table_data`);
            setTableData(Array.isArray(tData.data) ? tData.data : []);
        } catch (err) {
            toast.error("Failed to delete purchase record");
        } finally {
            setRecordToDelete(null);
        }
    };

    const handleDraftCostChange = (id, value) => {
        setDraftsList(prev =>
            prev.map(g => ({
                ...g,
                rows: g.rows.map(r => r.id === id ? { ...r, productCost: value } : r)
            }))
        );
    };

    const handleDraftCostBlur = async (id, value) => {
        try {
            await axios.put(`${PURCHASE_API}/pi/drafts/${id}/cost`, { productCost: value });
        } catch (err) {
            toast.error("Failed to update cost");
        }
    };

    const handleViewDraftChar = (row) => {
        const chars = row.characteristics && row.characteristics.length > 0
            ? row.characteristics
            : [{ characteristic: "Supplier Description", value: "" }];
        setDraftCharValues(chars);
        setShowDraftCharModal(true);
    };

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;

    const currentRows = filteredProducts.slice(
        indexOfFirstRow,
        indexOfLastRow
    );

    const totalPages = Math.ceil(
        filteredProducts.length / rowsPerPage
    );

    return (
        <Layout>
            <div style={styles.pageTitleBar}>
                <div>
                    <div style={styles.pageTitle}>Purchase Info Record</div>
                    <div style={styles.pageSubtitle}>Assign Business Partner</div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn btn-dark" style={styles.primaryBtn} onClick={() => setShowUploadModal(true)}>
                        Assign Business Partner by Upload
                    </button>
                    <button className="btn btn-dark" style={styles.primaryBtn} onClick={handleOpenBulkAssign}>
                        + Assign Business Partner
                    </button>
                </div>
            </div>

            <div style={styles.filterCard}>
                <div className="row g-3">
                    <div className="col-md-2">
                        <label style={styles.compactLabel}>Product Type</label>
                        <Select
                            styles={customSelectStyles}
                            options={[...new Map(tableData.filter(p => p.productType).map(p => [p.productType, { value: p.productType, label: p.productType }])).values()]}
                            value={filters.productType}
                            onChange={v => setFilters({ ...filters, productType: v })}
                            isClearable placeholder="Select Product Type..."
                        />
                    </div>
                </div>

                {/* ── Cable attribute filters — 2nd row, matches Purchase Requisition layout ── */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(9, minmax(90px, 1fr))", gap: 10, marginTop: 12 }}>
                    <div>
                        <label style={styles.compactLabel}>Construction</label>
                        <Select styles={customSelectStyles} options={constructionOptions} value={selectedConstruction}
                            onChange={setSelectedConstruction} isClearable placeholder="Construction"
                            menuPortalTarget={document.body} menuPosition="fixed" />
                    </div>
                    <div>
                        <label style={styles.compactLabel}>Code</label>
                        <Select styles={customSelectStyles} options={codeOptions} value={selectedCode}
                            onChange={setSelectedCode} isClearable placeholder="Code"
                            menuPortalTarget={document.body} menuPosition="fixed" />
                    </div>
                    <div>
                        <label style={styles.compactLabel}>Cable Type</label>
                        <Select styles={customSelectStyles} options={cableTypeOptions} value={selectedCableType}
                            onChange={setSelectedCableType} isClearable placeholder="Cable Type"
                            menuPortalTarget={document.body} menuPosition="fixed" />
                    </div>
                    <div>
                        <label style={styles.compactLabel}>Category</label>
                        <Select styles={customSelectStyles} options={cableCategoryOptions} value={selectedCableCategory}
                            onChange={setSelectedCableCategory} isClearable placeholder="Category"
                            menuPortalTarget={document.body} menuPosition="fixed" />
                    </div>
                    <div>
                        <label style={styles.compactLabel}>Cores</label>
                        <Select styles={customSelectStyles} options={coresOptions} value={selectedCores}
                            onChange={setSelectedCores} isClearable placeholder="Cores"
                            menuPortalTarget={document.body} menuPosition="fixed" />
                    </div>
                    <div>
                        <label style={styles.compactLabel}>Color</label>
                        <Select styles={customSelectStyles} options={colorOptions} value={selectedColor}
                            onChange={setSelectedColor} isClearable placeholder="Color"
                            menuPortalTarget={document.body} menuPosition="fixed" />
                    </div>
                    <div>
                        <label style={styles.compactLabel}>Cross Section</label>
                        <Select styles={customSelectStyles} options={crossSectionOptions} value={selectedCrossSection}
                            onChange={setSelectedCrossSection} isClearable placeholder="Cross Section"
                            menuPortalTarget={document.body} menuPosition="fixed" />
                    </div>
                    <div>
                        <label style={styles.compactLabel}>Std. Ref</label>
                        <Select styles={customSelectStyles} options={stdRefOptions} value={selectedStdRef}
                            onChange={setSelectedStdRef} isClearable placeholder="Std. Ref"
                            menuPortalTarget={document.body} menuPosition="fixed" />
                    </div>
                    <div>
                        <label style={styles.compactLabel}>Voltage</label>
                        <Select styles={customSelectStyles} options={voltageOptions} value={selectedVoltage}
                            onChange={setSelectedVoltage} isClearable placeholder="Voltage"
                            menuPortalTarget={document.body} menuPosition="fixed" />
                    </div>
                </div>

                <div className="row g-3 mt-1">
                    <div className="col-md-2">
                        <label style={styles.compactLabel}>Product Code</label>
                        <Select
                            styles={customSelectStyles}
                            options={[...new Map(tableData.map(p => [p.productCode, { value: p.productCode, label: p.productCode }])).values()]}
                            value={filters.code}
                            onChange={v => {
                                const product = tableData.find(
                                    p => p.productCode === v?.value
                                );

                                setFilters({
                                    ...filters,
                                    code: v,
                                    description: product
                                        ? {
                                            value: product.productDescription,
                                            label: product.productDescription
                                        }
                                        : null
                                });
                            }}
                            isClearable placeholder="Select Code..."
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.compactLabel}>Product Description</label>
                        <Select
                            styles={customSelectStyles}
                            options={[...new Map(tableData.map(p => [p.productDescription, { value: p.productDescription, label: p.productDescription }])).values()]}
                            value={filters.description}
                            onChange={v => {
                                const product = tableData.find(
                                    p => p.productDescription === v?.value
                                );

                                setFilters({
                                    ...filters,
                                    description: v,
                                    code: product
                                        ? {
                                            value: product.productCode,
                                            label: product.productCode
                                        }
                                        : null
                                });
                            }}
                            isClearable placeholder="Select Description..."
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.compactLabel}>Category</label>
                        <Select
                            styles={customSelectStyles}
                            options={[...new Map(tableData.filter(p => p.productCategory).map(p => [p.productCategory, { value: p.productCategory, label: p.productCategory }])).values()]}
                            value={filters.category}
                            onChange={v => setFilters({ ...filters, category: v, subCategory: null })}
                            isClearable placeholder="Select Category..."
                        />
                    </div>
                    <div className="col-md-2">
                        <label style={styles.compactLabel}>Sub Category</label>
                        <Select
                            styles={customSelectStyles}
                            options={subCategoryOptions}
                            value={filters.subCategory}
                            onChange={v => setFilters({ ...filters, subCategory: v })}
                            isClearable placeholder="Select Sub Category..."
                            noOptionsMessage={() => filters.category ? "No sub categories for this category" : "No options"}
                        />
                    </div>

                    <div className="col-md-2">
                        <label style={styles.compactLabel}>Supplier Code</label>
                        <Select
                            styles={customSelectStyles}
                            options={[
                                ...new Map(
                                    tableData
                                        .filter(p => p.vendorCode)
                                        .map(p => [
                                            p.vendorCode,
                                            {
                                                value: p.vendorCode,
                                                label: p.vendorCode
                                            }
                                        ])
                                ).values()
                            ]}
                            value={filters.vendorCode}
                            onChange={v =>
                                setFilters({
                                    ...filters,
                                    vendorCode: v
                                })
                            }
                            isClearable
                            placeholder="Select Supplier Code..."
                        />
                    </div>

                    <div className="col-md-2">
                        <label style={styles.compactLabel}>Supplier</label>
                        <Select
                            styles={customSelectStyles}
                            options={[
                                ...new Map(
                                    tableData
                                        .filter(p => p.vendor)
                                        .map(p => [
                                            p.vendor,
                                            {
                                                value: p.vendor,
                                                label: p.vendor
                                            }
                                        ])
                                ).values()
                            ]}
                            value={filters.vendor}
                            onChange={v =>
                                setFilters({
                                    ...filters,
                                    vendor: v
                                })
                            }
                            isClearable
                            placeholder="Select Supplier..."
                        />
                    </div>
                </div>
            </div>

            <style>{`
    .pi-tab-toggle { display: inline-flex; background: #f1f5f9; border-radius: 8px; padding: 3px; gap: 2px; margin-bottom: 14px; }
    .pi-tab-toggle button { border: none; background: transparent; border-radius: 6px; padding: 5px 20px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.15s ease; }
    .pi-tab-toggle button.pi-active-tab { background: #fff; color: #1e293b; box-shadow: 0 1px 3px rgba(0,0,0,0.12); }
`}</style>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div className="pi-tab-toggle">
                    <button className={assignmentTab === "unassigned" ? "pi-active-tab" : ""} onClick={() => setAssignmentTab("unassigned")}>
                        Not Assigned
                    </button>
                    <button className={assignmentTab === "assigned" ? "pi-active-tab" : ""} onClick={() => setAssignmentTab("assigned")}>
                        Assigned
                    </button>
                </div>
            </div>

            <div style={styles.tableContainer}>
                <table className="table" style={styles.erpTable}>
                    <thead>
                        <tr>
                            <th style={{ ...styles.erpTh, width: '40px', textAlign: 'center' }}>
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    disabled={
                                        filters.code ||
                                        filters.description ||
                                        filters.productType ||
                                        filters.category ||
                                        filters.subCategory ||
                                        filters.vendorCode ||
                                        filters.vendor
                                    }
                                    checked={
                                        filteredProducts.length > 0 &&
                                        selectedRows.length === filteredProducts.length
                                    }
                                />
                            </th>
                            <th style={{ ...styles.erpTh, width: '130px' }}>Product Code</th>
                            <th style={{ ...styles.erpTh, width: '220px' }}>Description</th>
                            <th style={{ ...styles.erpTh, width: '150px' }}>Category</th>
                            <th style={{ ...styles.erpTh, width: '120px' }}>Sub Category</th>
                            <th style={{ ...styles.erpTh, width: '100px' }}>Supplier Data</th>
                            <th style={{ ...styles.erpTh, width: '60px' }}>UOM</th>
                            <th style={{ ...styles.erpTh, width: '80px' }}>MOQ</th>
                            <th style={{ ...styles.erpTh, width: '120px' }}>Supplier Code</th>
                            <th style={{ ...styles.erpTh, width: '180px' }}>Supplier</th>
                            <th style={{ ...styles.erpTh, width: '100px' }}>Base Unit</th>
                            <th style={{ ...styles.erpTh, width: '100px', textAlign: 'right' }}>Unit Price</th>
                            <th style={{ ...styles.erpTh, width: '130px', textAlign: 'center' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredProducts.length > 0 ? currentRows.map((p, i) => (
                            <tr key={i} style={{ transition: 'background 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                                <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                    <input type="checkbox" checked={selectedRows.includes(p.productCode)}
                                        onChange={() => handleSelectRow(p.productCode)} />
                                </td>
                                <td style={styles.erpTd}>
                                    <span style={{ fontWeight: 500, color: '#0f172a', fontFamily: 'monospace', fontSize: 12 }}>
                                        {p.productCode}
                                    </span>
                                </td>
                                <td style={styles.erpTd} title={p.productDescription}>{p.productDescription || '-'}</td>
                                <td style={styles.erpTd}>
                                    {p.productCategory || '-'}
                                </td>

                                <td style={styles.erpTd}>
                                    {p.subCategory || '-'}
                                </td>
                                <td style={styles.erpTd}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span>{p.productClass || '-'}</span>
                                        {p.productClass && (
                                            <span
                                                style={{ cursor: 'pointer', color: '#475569', fontSize: 11 }}
                                                onClick={() => openClassCharacteristics(p)}
                                                title="View Class Details"
                                            >▶</span>
                                        )}
                                    </div>
                                </td>
                                <td style={{ ...styles.erpTd, color: '#64748b' }}>{p.uom || '-'}</td>
                                <td style={styles.erpTd}>
                                    {p.moq || '-'}
                                </td>
                                <td style={styles.erpTd}>
                                    {p.vendorCode || '-'}
                                </td>
                                <td style={styles.erpTd} title={p.vendor}>
                                    {p.vendor ? (
                                        <div>
                                            {p.vendor || '-'}
                                        </div>
                                    ) : (
                                        <span style={{ color: '#cbd5e1' }}>—</span>
                                    )}
                                </td>
                                <td style={styles.erpTd}>
                                    {p.baseUnit || '-'}
                                </td>
                                <td style={{ ...styles.erpTd, textAlign: 'right', fontWeight: 500 }}>
                                    {p.productCost ? `₹ ${parseFloat(p.productCost).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-'}
                                </td>
                                <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                                        <button
                                            className="btn btn-sm btn-outline-dark"
                                            style={{ fontSize: '11px', padding: '3px 10px', borderRadius: 4 }}
                                            onClick={() => handleManageSingleProduct(p)}
                                        >
                                            View
                                        </button>
                                        {p.pir_number && (
                                            <button
                                                title="Delete this purchase record"
                                                style={{ background: "#fff", border: "1px solid #f5c2c7", borderRadius: "4px", padding: "4px 8px", cursor: "pointer", display: "inline-flex" }}
                                                onClick={() => handleDeleteRecord(p.pir_number, p.productCode)}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan="10" className="text-center text-muted p-4">
                                    No products found matching the criteria
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "10px",
                        padding: "15px"
                    }}
                >
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                        Previous
                    </button>

                    <span style={{ padding: "5px 10px" }}>
                        Page {currentPage} of {totalPages || 1}
                    </span>

                    <button
                        className="btn btn-sm btn-outline-secondary"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>

            {/* --- 1. SINGLE ROW MANAGE PARTNERS MODAL --- */}
            {showManageModal && activeProduct && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "1400px", width: "98%", maxHeight: "90vh" }}>
                        <div style={styles.modalHeader}>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>
                                    Manage Partners
                                </div>

                            </div>
                            <button style={styles.iconBtn} onClick={() => { setShowManageModal(false); setIsSingleEditMode(false); }}>✕</button>
                        </div>

                        <div style={styles.modalBody}>
                            <table className="table" style={{ ...styles.erpTable }}>
                                <thead>
                                    <tr>

                                        <th style={styles.erpTh}>Product Code</th>
                                        <th style={styles.erpTh}>Product Description</th>
                                        <th style={styles.erpTh}>PIR No.</th>
                                        <th style={{ ...styles.erpTh, width: '100px' }}>
                                            Supplier Data
                                        </th>

                                        <th style={{ ...styles.erpTh, width: '250px' }}>Supplier</th>
                                        <th style={{ ...styles.erpTh, width: '100px' }}>
                                            Base Unit
                                        </th>
                                        <th style={{ ...styles.erpTh, width: '80px' }}>MOQ</th>
                                        <th style={{ ...styles.erpTh, width: '130px' }}>Unit Cost</th>
                                        <th style={styles.erpTh}>UOM</th>
                                        <th style={{ ...styles.erpTh, textAlign: 'center', width: '80px' }}>Default</th>
                                        {isSingleEditMode && (
                                            <th style={{ ...styles.erpTh, textAlign: 'center', width: '160px' }}></th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {singleAssignRows.map((row) => (
                                        <tr key={row.id}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <td
                                                style={{
                                                    ...styles.erpTd,
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {activeProduct.productCode}
                                            </td>

                                            <td style={styles.erpTd}>
                                                {activeProduct.productDescription}
                                            </td>

                                            <td
                                                style={{
                                                    ...styles.erpTd,
                                                    fontFamily: 'monospace',
                                                    fontSize: 12,
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {row.pirNumber || '—'}
                                            </td>
                                            <td style={styles.erpTd}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span>{row.productClass || '—'}</span>
                                                    {row.productClass && (
                                                        <span
                                                            style={{ cursor: 'pointer', color: '#475569', fontSize: 11 }}
                                                            onClick={() => handleOpenCharModal('single', row.id, row)}
                                                            title={isSingleEditMode ? "Edit Characteristics" : "View Characteristics"}>▶
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ ...styles.erpTd, overflow: 'visible' }}>
                                                {isSingleEditMode ? (
                                                    <Select
                                                        styles={customSelectStyles}
                                                        options={getOptions(masterData.vendors)}
                                                        value={row.vendor}
                                                        onChange={v => handleAssignRowChange(row.id, 'vendor', v, true)}
                                                        placeholder="Select Supplier"

                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                    />
                                                ) : (
                                                    <span>{row.vendor?.label || '—'}</span>
                                                )}
                                            </td>
                                            <td style={styles.erpTd}>
                                                {row.baseUnit || 0}
                                            </td>

                                            <td style={styles.erpTd}>
                                                {row.moq || 0}
                                            </td>



                                            <td style={styles.erpTd}>
                                                {isSingleEditMode ? (
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        style={styles.compactInput}
                                                        value={row.productCost}
                                                        onChange={(e) => handleAssignRowChange(row.id, 'productCost', e.target.value, true)}
                                                    />
                                                ) : (
                                                    <span style={{ fontWeight: 500 }}>
                                                        {row.productCost ? `₹ ${parseFloat(row.productCost).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ ...styles.erpTd, color: '#64748b' }}>{row.uom}</td>
                                            <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                {isSingleEditMode ? (
                                                    <input
                                                        type="radio"
                                                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                        name={`default_${row.productCode}_single`}
                                                        checked={row.isDefault || false}
                                                        onChange={() => handleSetDefault(row.id, row.productCode, true)}
                                                    />
                                                ) : (
                                                    row.isDefault
                                                        ? <span style={{ background: '#f1f5f9', color: '#334155', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, border: '1px solid #e2e8f0' }}>Default</span>
                                                        : <span style={{ color: '#cbd5e1' }}>—</span>
                                                )}
                                            </td>
                                            {isSingleEditMode && (
                                                <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                    <button
                                                        className="btn btn-sm btn-outline-dark me-2"
                                                        style={{ fontSize: '11px', padding: '3px 8px' }}
                                                        onClick={handleAddSingleRow}>+ Vendor
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        style={{ fontSize: '11px', padding: '3px 8px' }}
                                                        onClick={() => handleRemoveAssignRow(row.id, true)}>Remove
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                    {singleAssignRows.length === 0 && (
                                        <tr>
                                            <td colSpan={isSingleEditMode ? 9 : 8} className="text-center p-4 text-muted">
                                                No vendors assigned yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div style={styles.modalFooter}>
                            <button className="btn btn-outline-secondary" onClick={() => { setShowManageModal(false); setIsSingleEditMode(false); }}>
                                Close
                            </button>
                            {isSingleEditMode ? (
                                <button className="btn btn-dark" onClick={handleSaveSingleManage}>Save</button>
                            ) : (
                                <button className="btn btn-dark" onClick={() => setIsSingleEditMode(true)}>Edit</button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* --- 2. BULK ASSIGN MODAL --- */}
            {showAssignModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, maxWidth: "1200px", width: "95%", maxHeight: "90vh" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>Assign Business Partner</div>
                            <button style={styles.iconBtn} onClick={() => { setShowAssignModal(false); setIsBulkEditMode(false); }}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            <table className="table" style={styles.erpTable}>
                                <thead>
                                    <tr>
                                        <th style={styles.erpTh}>Product Code</th>
                                        <th style={styles.erpTh}>Product Description</th>
                                        <th style={{ ...styles.erpTh, width: '100px' }}>Class</th>
                                        <th style={{ ...styles.erpTh, width: '250px' }}>Supplier</th>
                                        <th style={{ ...styles.erpTh, width: '120px' }}>Cost</th>
                                        <th style={styles.erpTh}>UOM</th>
                                        <th style={{ ...styles.erpTh, textAlign: 'center', width: '80px' }}>Default</th>
                                        {isBulkEditMode && (
                                            <th style={{ ...styles.erpTh, textAlign: 'center', width: '160px' }}></th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {assignRows.map((row) => (
                                        <tr key={row.id}
                                            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <td style={styles.erpTd}>{row.productCode}</td>
                                            <td style={styles.erpTd}>{row.productDescription}</td>
                                            <td style={styles.erpTd}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span>{row.productClass || '—'}</span>
                                                    {row.productClass && isBulkEditMode && (
                                                        <span
                                                            style={{ cursor: 'pointer', color: '#475569', fontSize: 11 }}
                                                            onClick={() => handleOpenCharModal('bulk', row.id, row)}
                                                            title="Edit Characteristics">▶
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Vendor */}
                                            <td style={{ ...styles.erpTd, overflow: 'visible' }}>
                                                {isBulkEditMode ? (
                                                    <Select
                                                        styles={customSelectStyles}
                                                        options={getOptions(masterData.vendors)}
                                                        value={row.vendor}
                                                        onChange={v => handleAssignRowChange(row.id, 'vendor', v, false)}
                                                        placeholder="Select Supplier"

                                                        menuPortalTarget={document.body}
                                                        menuPosition={'fixed'}
                                                    />
                                                ) : (
                                                    <span>{row.vendor?.label || '—'}</span>
                                                )}
                                            </td>

                                            {/* Cost */}
                                            <td style={styles.erpTd}>
                                                {isBulkEditMode ? (
                                                    <input type="number" className="form-control" style={styles.compactInput}
                                                        value={row.productCost}
                                                        onChange={(e) => handleAssignRowChange(row.id, 'productCost', e.target.value, false)} />
                                                ) : (
                                                    <span style={{ fontWeight: 500 }}>
                                                        {row.productCost ? `₹ ${parseFloat(row.productCost).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                                                    </span>
                                                )}
                                            </td>

                                            <td style={{ ...styles.erpTd, color: '#64748b' }}>{row.uom}</td>

                                            {/* Default */}
                                            <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                {isBulkEditMode ? (
                                                    <input
                                                        type="radio"
                                                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                        name={`default_${row.productCode}_bulk`}
                                                        checked={row.isDefault || false}
                                                        onChange={() => handleSetDefault(row.id, row.productCode, false)}
                                                        title="Mark as Default Vendor"
                                                    />
                                                ) : (
                                                    row.isDefault
                                                        ? <span style={{ background: '#f1f5f9', color: '#334155', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, border: '1px solid #e2e8f0' }}>Default</span>
                                                        : <span style={{ color: '#cbd5e1' }}>—</span>
                                                )}
                                            </td>

                                            {/* Actions - edit mode only */}
                                            {isBulkEditMode && (
                                                <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                    <button className="btn btn-sm btn-outline-dark me-2"
                                                        style={{ fontSize: '11px', padding: '4px 8px' }}
                                                        onClick={() => handleAddBulkRow(row.productCode)}>+ Vendor</button>
                                                    <button className="btn btn-sm btn-outline-danger"
                                                        style={{ fontSize: '11px', padding: '4px 8px' }}
                                                        onClick={() => handleRemoveAssignRow(row.id, false)}>Remove</button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                    {assignRows.length === 0 && (
                                        <tr><td colSpan={isBulkEditMode ? 8 : 7} className="text-center p-3 text-muted">All rows removed.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div style={styles.modalFooter}>
                            <button className="btn btn-outline-secondary" onClick={() => { setShowAssignModal(false); setIsBulkEditMode(false); }}>Close</button>
                            {isBulkEditMode ? (
                                <button className="btn btn-dark" onClick={handleSaveBulkAssignments}>Save</button>
                            ) : (
                                <button className="btn btn-dark" onClick={() => setIsBulkEditMode(true)}>Edit</button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* --- UPLOAD MODAL --- */}
            {showUploadModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ background: "#fff", width: 450, borderRadius: 12, padding: 25 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
                            <h4 style={{ margin: 0 }}>Upload Purchase Info</h4>
                            <button onClick={() => setShowUploadModal(false)} style={styles.iconBtn}>✕</button>
                        </div>
                        <div style={{ border: "2px dashed #cbd5e1", borderRadius: 8, padding: 20, background: "#f8fafc", textAlign: "center" }}>
                            <a href="/assets/files/Purchase_Info_Upload_Template.xlsx" download
                                style={{ background: "#021843", color: "#fff", padding: "10px 24px", borderRadius: 6, textDecoration: "none", fontSize: 13, fontWeight: 600, display: "inline-block", marginBottom: 12 }}>
                                Download Template
                            </a>
                            <div>
                                <input type="file" id="purchaseExcelUpload" accept=".xlsx,.xls,.csv"
                                    style={{ display: "none" }} onChange={handlePurchaseExcelUpload} />
                                <label htmlFor="purchaseExcelUpload"
                                    style={{ background: "#021843", color: "#fff", padding: "10px 24px", borderRadius: 6, cursor: "pointer", fontWeight: 600, display: "inline-block" }}>
                                    Browse File
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- PENDING DRAFTS PREVIEW MODAL --- */}
            {showDraftModal && (
                <div style={styles.modalBackdrop}>
                    <div style={{ ...styles.modal, width: "98%", maxWidth: 1400, maxHeight: "90vh" }}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 600, fontSize: 18 }}>Pending Purchase Info Drafts</div>
                            <button onClick={() => setShowDraftModal(false)} style={styles.iconBtn}>✕</button>
                        </div>
                        <div style={styles.modalBody}>
                            {draftsList.length === 0 ? (
                                <p style={{ textAlign: "center", color: "#64748b", marginTop: 40 }}>No pending drafts.</p>
                            ) : (
                                <table className="table" style={styles.erpTable}>
                                    <thead>
                                        <tr>
                                            <th style={styles.erpTh}>Product Code</th>
                                            <th style={styles.erpTh}>Product Description</th>
                                            <th style={{ ...styles.erpTh, width: '90px' }}>PIR No.</th>
                                            <th style={{ ...styles.erpTh, width: '100px' }}>Supplier Data</th>
                                            <th style={{ ...styles.erpTh, width: '230px' }}>Supplier</th>
                                            <th style={{ ...styles.erpTh, width: '90px' }}>Base Unit</th>
                                            <th style={{ ...styles.erpTh, width: '70px' }}>MOQ</th>
                                            <th style={{ ...styles.erpTh, width: '120px' }}>Unit Cost</th>
                                            <th style={{ ...styles.erpTh, width: '70px' }}>UOM</th>
                                            <th style={{ ...styles.erpTh, textAlign: 'center', width: '80px' }}>Default</th>
                                            <th style={{ ...styles.erpTh, textAlign: 'center', width: '160px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {draftsList.flatMap(group => {
                                            const product = tableData.find(p => p.productCode === group.productCode);
                                            return group.rows.map(r => (
                                                <tr key={r.id}>
                                                    <td style={styles.erpTd}>
                                                        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{group.productCode}</span>
                                                        {conflictProductCodes.includes(group.productCode) && (
                                                            <div style={{ fontSize: 10, color: "#b45309", background: "#fef3c7", padding: "1px 6px", borderRadius: 4, marginTop: 4, display: "inline-block" }}>
                                                                Will replace existing
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={styles.erpTd}>{group.productDescription}</td>
                                                    <td style={{ ...styles.erpTd, fontFamily: 'monospace', fontSize: 12 }}>—</td>
                                                    <td style={styles.erpTd}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                            <span>{r.productClass || '—'}</span>
                                                            {r.characteristics && r.characteristics.length > 0 && (
                                                                <span
                                                                    style={{ cursor: 'pointer', color: '#475569', fontSize: 11 }}
                                                                    onClick={() => handleViewDraftChar(r)}
                                                                    title="View Supplier Description">▶
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td style={styles.erpTd}>{r.vendorName}</td>
                                                    <td style={styles.erpTd}>{r.baseUnit || 0}</td>
                                                    <td style={styles.erpTd}>{product?.moq || 0}</td>
                                                    <td style={styles.erpTd}>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            style={styles.compactInput}
                                                            value={r.productCost || ''}
                                                            onChange={(e) => handleDraftCostChange(r.id, e.target.value)}
                                                            onBlur={(e) => handleDraftCostBlur(r.id, e.target.value)}
                                                        />
                                                    </td>
                                                    <td style={{ ...styles.erpTd, color: '#64748b' }}>{r.uom}</td>
                                                    <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                        {r.isDefault
                                                            ? <span style={{ background: '#f1f5f9', color: '#334155', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, border: '1px solid #e2e8f0' }}>Default</span>
                                                            : <span style={{ color: '#cbd5e1' }}>—</span>}
                                                    </td>
                                                    <td style={{ ...styles.erpTd, textAlign: 'center' }}>
                                                        <button
                                                            className="btn btn-sm btn-success me-2"
                                                            style={{ fontSize: '11px', padding: '3px 10px' }}
                                                            disabled={publishingProductCode === group.productCode || isPublishingAll}
                                                            onClick={() => onClickPublishPurchaseDraft(group.productCode)}
                                                        >
                                                            {publishingProductCode === group.productCode ? "Saving..." : "Save"}
                                                        </button>
                                                        <button
                                                            title="Delete this supplier row"
                                                            style={{ background: "#fff", border: "1px solid #f5c2c7", borderRadius: "4px", padding: "4px 8px", cursor: "pointer", display: "inline-flex" }}
                                                            onClick={() => handleDeletePurchaseDraftRow(r.id)}
                                                        >
                                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                                                                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                                                            </svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ));
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        {draftsList.length > 0 && (
                            <div style={styles.modalFooter}>
                                <button className="btn btn-success" disabled={isPublishingAll} onClick={handlePublishAllPurchaseDrafts}>
                                    {isPublishingAll ? "Saving All..." : "Save All"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* --- SUPPLIER DESCRIPTION POPUP (▶ on Supplier Data) --- */}
            <CharacteristicsModal
                modalVisible={showDraftCharModal}
                selectedCharacteristics={draftCharValues}
                isEditing={false}
                handleChange={() => { }}
                handleClose={() => setShowDraftCharModal(false)}
                title="Supplier Description"
            />

            {/* --- SINGLE-PRODUCT REPLACE CONFIRM --- */}
            {showDraftReplaceConfirm && (
                <div style={styles.modalBackdrop}>
                    <div style={{ background: "#fff", width: 420, borderRadius: 14, padding: "36px 32px", textAlign: "center" }}>
                        <h4>Product Already Has Vendors</h4>
                        <p style={{ color: "#64748b" }}>
                            Product "{draftReplaceTarget}" already has assigned vendor(s). Publishing this draft will replace them. Continue?
                        </p>
                        <button className="btn btn-success" style={{ width: "100%", marginBottom: 10 }}
                            onClick={() => { const c = draftReplaceTarget; setShowDraftReplaceConfirm(false); handlePublishPurchaseDraft(c, true); }}>
                            Yes, Replace
                        </button>
                        <button className="btn btn-light" style={{ width: "100%" }}
                            onClick={() => setShowDraftReplaceConfirm(false)}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* --- SAVE-ALL REPLACE CONFIRM --- */}
            {showBulkReplaceConfirm && (
                <div style={styles.modalBackdrop}>
                    <div style={{ background: "#fff", width: 460, borderRadius: 14, padding: "36px 32px", textAlign: "center" }}>
                        <h4>Some Products Already Have Vendors</h4>
                        <p style={{ color: "#64748b" }}>
                            {draftsList.filter(d => conflictProductCodes.includes(d.productCode)).map(d => d.productCode).join(", ")}{" "}
                            already have assigned vendors. Continuing will replace them. Proceed?
                        </p>
                        <button className="btn btn-success" style={{ width: "100%", marginBottom: 10 }}
                            onClick={() => { setShowBulkReplaceConfirm(false); publishAllPurchaseDraftsApi(true); }}>
                            Yes, Replace All
                        </button>
                        <button className="btn btn-light" style={{ width: "100%" }}
                            onClick={() => setShowBulkReplaceConfirm(false)}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}



            {/* --- CLASS CHARACTERISTICS MODAL (main table ▶ button) --- */}
            <CharacteristicsModal
                modalVisible={showClassModal}
                selectedCharacteristics={classCharValues}
                isEditing={true}
                handleChange={handleClassCharChange}
                handleClose={handleCloseClassModal}
                onSave={handleSaveClassCharacteristics}
                title="Characteristics"
            />

            {/* --- CHARACTERISTICS MODAL (Manage Partners / Bulk Assign ▶ button) --- */}
            <CharacteristicsModal
                modalVisible={charModalVisible}
                selectedCharacteristics={selectedChars}
                isEditing={isSingleEditMode || isBulkEditMode}
                handleChange={handleCharChange}
                handleClose={handleCloseCharModal}
                onSave={handleSaveCharModal}
                title={charModalContext.pirNumber ? `Characteristics — ${charModalContext.pirNumber}` : 'Characteristics'}
            />

            {/* DELETE PURCHASE RECORD CONFIRM */}
            {showDeleteRecordConfirm && (
                <div style={styles.modalBackdrop}>
                    <div style={{ background: "#fff", width: 420, borderRadius: 14, padding: "36px 32px", textAlign: "center" }}>
                        <h4>Delete Purchase Record</h4>
                        <p style={{ color: "#64748b" }}>
                            Delete the purchase record for "{recordToDelete?.productCode}"? This removes it from the list but keeps history in the database.
                        </p>
                        <button className="btn btn-danger" style={{ width: "100%", marginBottom: 10 }}
                            onClick={confirmDeleteRecord}>
                            Yes, Delete
                        </button>
                        <button className="btn btn-light" style={{ width: "100%" }}
                            onClick={() => { setShowDeleteRecordConfirm(false); setRecordToDelete(null); }}>
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* ===================== FLOATING DRAFTS BADGE ===================== */}
            <div style={{ position: 'fixed', bottom: 30, right: 30, display: 'flex', flexDirection: 'column', gap: 15, zIndex: 9999 }}>
                {draftsList.length > 0 && (
                    <div
                        onClick={() => setShowDraftModal(true)}
                        style={{
                            background: '#1e293b',
                            color: '#fff',
                            padding: '12px 20px',
                            borderRadius: 50,
                            cursor: 'pointer',
                            boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10
                        }}
                    >
                        <span style={{ fontWeight: 'bold', fontSize: 15 }}>📂 Pending Drafts</span>
                        <span style={{ background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 'bold' }}>
                            {draftsList.reduce((sum, g) => sum + g.rows.length, 0)}
                        </span>
                    </div>
                )}
            </div>

            <ToastContainer position="top-right" autoClose={3000} />


            <ToastContainer position="top-right" autoClose={3000} />
        </Layout>
    );
}

const customSelectStyles = {
    control: (base) => ({
        ...base, minHeight: 32, height: 32, fontSize: '13px', borderColor: '#dee2e6',
        boxShadow: 'none', '&:hover': { borderColor: '#b3b3b3' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '32px', display: 'flex', alignItems: 'center' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
    menu: (base) => ({ ...base, fontSize: '13px', zIndex: 9999 }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#1e293b' : state.isFocused ? '#f1f5f9' : 'white',
        color: state.isSelected ? 'white' : '#334155',
        cursor: 'pointer', fontSize: '13px'
    })
};

const styles = {
    compactLabel: { fontSize: "12px", fontWeight: 600, marginBottom: "2px", color: '#334155' },
    compactInput: { fontSize: "13px", height: "32px", padding: "4px 8px", border: '1px solid #dee2e6', borderRadius: '4px' },
    filterCard: { background: '#ffffff', borderRadius: 8, padding: 15, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 15 },
    tableContainer: {
        background: "#fff",
        borderRadius: "12px",
        padding: "20px",
        border: "1px solid #e5e7eb",
        overflowX: "auto"
    }, erpTable: {
        width: "100%",
        borderCollapse: "collapse",
        background: "#fff"
    },

    erpTh: {
        background: "#ffffff",
        fontSize: "13px",
        fontWeight: "600",
        color: "#333",
        padding: "12px 10px",
        borderBottom: "1px solid #e5e7eb",
        textAlign: "left"
    },

    erpTd: {
        fontSize: "13px",
        color: "#444",
        padding: "16px 10px",
        borderBottom: "1px solid #f0f0f0",
        verticalAlign: "middle",
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: "1.4"
    },
    pageTitleBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
    pageTitle: { fontSize: 20, fontWeight: 700, color: '#1e293b' },
    pageSubtitle: { fontSize: 12, color: '#64748b' },
    primaryBtn: { padding: '6px 14px', fontSize: '13px' },
    modalBackdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 },
    modal: { background: '#fff', borderRadius: '8px', maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
    modalHeader: { padding: '12px 20px', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    iconBtn: { background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer' },
    modalBody: { padding: '20px', overflowY: 'auto' },
    modalFooter: { padding: '12px 20px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '10px' },


};

// Error boundary: a render error shows a message instead of a blank white page
class PurchaseInfoErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, message: '' };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, message: error && error.message ? error.message : String(error) };
    }
    componentDidCatch(error, info) {
        console.error('PurchaseInfo crashed:', error, info);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: 24 }}>
                    <h3>Something went wrong on the Purchase Info page.</h3>
                    <p style={{ color: '#b00020' }}>{this.state.message}</p>
                    <button onClick={() => window.location.reload()}>Reload</button>
                </div>
            );
        }
        return this.props.children;
    }
}

function PurchaseInfoWithBoundary() {
    return (
        <PurchaseInfoErrorBoundary>
            <PurchaseInfo />
        </PurchaseInfoErrorBoundary>
    );
}

export default PurchaseInfoWithBoundary;